import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { App, AppSource } from '@hangar/shared';
import { toast } from 'sonner';
import { scmUnsupported, useScmConnections, useScmRepositories } from '@/api/hooks';
import { useAppSourceMutations } from '@/api/hooksSource';
import { stagger } from '@/lib/motion';
import { useCanConfigure } from '@/lib/permissions';
import { FormField, fieldA11y } from '@/components/FormField';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { pickById, repoError, repoHint, repoLabel, showsRepoFilter, usableConnections } from '@/features/source-control/bitriseModel';
import { ORIGIN_LABEL, canUnlink, linkBody, runnerLine, showsRepositoryCard } from './repositoryCardModel';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** The workspace (connection) and the repository picked, with the same pickers "Create on Bitrise" uses. */
function useRepositoryPick() {
  const connections = useScmConnections();
  const usable = usableConnections(connections.data);
  const [connectionId, setConnectionId] = useState<string | undefined>();
  const connection = pickById(usable, connectionId);
  const [filter, setFilter] = useState('');
  const repos = useScmRepositories(connection?.id, useDebounced(filter.trim(), 300));
  const [repository, setRepository] = useState('');
  const selectConnection = (id: string) => {
    setConnectionId(id);
    setRepository('');
  };
  return { connections, usable, connection, selectConnection, filter, setFilter, repos, repository, setRepository };
}
type Pick_ = ReturnType<typeof useRepositoryPick>;

/**
 * The app's repository (Expo / React Native apps): Hangar's runner fetches it at the picked commit for Run build
 * (`hangar_runner`) and verifies protected branches against it. Linking asks Bitbucket first (`PUT /apps/{id}/source`).
 */
export function RepositoryCard({ app }: { app: App }) {
  const show = showsRepositoryCard(app.framework);
  const pick = useRepositoryPick();
  if (!show || (pick.connections.isError && scmUnsupported(pick.connections.error))) return null;
  return (
    <Card className="enter" style={stagger(2)} data-testid="app-repository-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="account_tree" size={18} className="text-subtle" />
          Repository
          {app.source ? <Tag>{ORIGIN_LABEL[app.source.origin]}</Tag> : <Tag>not linked</Tag>}
        </CardTitle>
        <CardDescription>{runnerLine(app.source)}</CardDescription>
      </CardHeader>
      <CardContent>{app.source ? <Linked app={app} source={app.source} /> : <LinkForm app={app} pick={pick} />}</CardContent>
    </Card>
  );
}

function Linked({ app, source }: { app: App; source: AppSource }) {
  const can = useCanConfigure(app.projectId);
  const { unlink } = useAppSourceMutations();
  return (
    <div className="flex flex-col gap-3" data-testid="app-repository-linked">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Icon name="commit" size={16} className="shrink-0 text-subtle" />
        <span className="min-w-0 truncate font-mono text-[13px] text-foreground" data-testid="app-repository-name">{source.fullName}</span>
        {source.defaultBranch ? <Tag>{source.defaultBranch}</Tag> : null}
      </div>
      {canUnlink(source) ? (
        <div>
          <GatedButton size="sm" variant="outline" data-testid="app-repository-unlink" can={can} loading={unlink.isPending} onClick={() => unlink.mutate({ appId: app.id }, { onSuccess: () => toast.success('Repository unlinked') })}>
            <Icon name="cancel" size={16} />
            Unlink
          </GatedButton>
        </div>
      ) : null}
      <ProblemAlert error={unlink.error} testId="app-repository-problem" />
    </div>
  );
}

function LinkForm({ app, pick }: { app: App; pick: Pick_ }) {
  const can = useCanConfigure(app.projectId);
  const { link } = useAppSourceMutations();
  if (pick.connections.isLoading) return <p className="text-xs text-subtle">Loading connections…</p>;
  if (pick.usable.length === 0) return <NoConnection />;
  const body = linkBody(pick.connection, pick.repository);
  const submit = () => {
    if (body) link.mutate({ appId: app.id, body }, { onSuccess: (a) => toast.success('Repository linked', { description: a.source?.fullName }) });
  };
  return (
    <div className="flex flex-col gap-3" data-testid="app-repository-form">
      {pick.usable.length > 1 ? <ConnectionSelect appId={app.id} pick={pick} /> : null}
      <RepositoryField appId={app.id} pick={pick} />
      <div>
        <GatedButton size="sm" data-testid="app-repository-link" can={can} blockedReason={!body ? 'Pick the repository first' : undefined} loading={link.isPending} onClick={submit}>
          <Icon name="add" size={16} />
          Link repository
        </GatedButton>
      </div>
      <ProblemAlert error={link.error} testId="app-repository-problem" />
    </div>
  );
}

function NoConnection() {
  return (
    <div className="flex flex-col gap-1.5 text-[13px]" data-testid="app-repository-no-connection">
      <span>Connect Bitbucket first: a repository or workspace access token with read access is all Hangar needs.</span>
      <Button asChild size="sm" variant="outline" className="w-fit">
        <Link to="/integrations">
          Open Integrations
          <Icon name="arrow_forward" size={16} />
        </Link>
      </Button>
    </div>
  );
}

function ConnectionSelect({ appId, pick }: { appId: string; pick: Pick_ }) {
  return (
    <FormField id={`repo-connection-${appId}`} label="Bitbucket workspace">
      <OptionSelect
        id={`repo-connection-${appId}`}
        value={pick.connection?.id ?? ''}
        onValueChange={pick.selectConnection}
        placeholder="Pick"
        options={pick.usable.map((c) => ({ value: c.id, label: `${c.name} · ${c.repository ? `${c.workspace}/${c.repository}` : c.workspace}` }))}
      />
    </FormField>
  );
}

function RepositoryField({ appId, pick }: { appId: string; pick: Pick_ }) {
  const { repos } = pick;
  const id = `repo-repository-${appId}`;
  return (
    <FormField id={id} label="Repository" required hint={repoHint(repos.data)} error={repoError(repos.isError, repos.error)}>
      <div className="flex flex-col gap-2">
        {showsRepoFilter(repos.data, pick.filter) ? <Input value={pick.filter} onChange={(e) => pick.setFilter(e.target.value)} placeholder="Filter by name" autoComplete="off" /> : null}
        <OptionSelect
          data-testid="app-repository-select"
          value={pick.repository}
          disabled={repos.isLoading}
          onValueChange={pick.setRepository}
          placeholder={repos.isLoading ? 'Loading repositories…' : 'Select a repository'}
          options={(repos.data?.data ?? []).map((r) => ({ value: r.slug, label: repoLabel(r) }))}
          {...fieldA11y(id, repos.isError ? 'error' : undefined, 'hint')}
        />
      </div>
    </FormField>
  );
}
